import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { useAuth } from '@/lib/hooks/useAuth';
import { isDemo } from '@/lib/demo/demoMode';
import { isEmbeddedPreview, getParentAdminToken } from '@/lib/utils/tokenStorage';
import { APP_PAGES } from '@/lib/appRegistry';

// Site-wide change-request button (owner/admin/pm) — on every admin page incl.
// /dev. The annotation suite (#68 + #71): point at an element, drag a region,
// or drop a marker; note each; and a screenshot of the annotated page is
// captured and attached to the request.

type Tool = 'point' | 'region' | 'marker';
interface Pin {
  id: number; kind: Tool; label: string; note: string;
  rect: { x: number; y: number; w: number; h: number }; // marker = 0-size point
}

function describe(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 40);
  const aria = el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('placeholder');
  if (aria) return `${tag} "${aria.slice(0, 40)}"`;
  if (text) return `${tag} "${text}"`;
  const cls = (el.getAttribute('class') || '').split(' ').filter(Boolean).slice(0, 2).join('.');
  return cls ? `${tag}.${cls}` : tag;
}

export function FloatingRequestButton() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // picker
  const [picking, setPicking] = useState(false);
  const [tool, setTool] = useState<Tool>('point');
  const [pins, setPins] = useState<Pin[]>([]);
  const [hover, setHover] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x: number; y: number } | null>(null);
  const pinSeq = useRef(1);
  const toolRef = useRef(tool);
  toolRef.current = tool;

  useEffect(() => {
    if (!picking) return;
    const isUI = (el: Element | null) => !el || !!el.closest('[data-cr-ui]');

    const onMove = (e: MouseEvent) => {
      if (toolRef.current === 'point') {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (isUI(el)) { setHover(null); return; }
        const r = el!.getBoundingClientRect();
        setHover({ x: r.left, y: r.top, w: r.width, h: r.height });
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY } : d);
    };
    const onDown = (e: MouseEvent) => {
      if (toolRef.current !== 'region') return;
      if (isUI(document.elementFromPoint(e.clientX, e.clientY))) return;
      e.preventDefault(); e.stopPropagation();
      setDrag({ x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY });
    };
    const onUp = (e: MouseEvent) => {
      if (toolRef.current === 'region' && drag) {
        const x = Math.min(drag.x0, e.clientX), y = Math.min(drag.y0, e.clientY);
        const w = Math.abs(e.clientX - drag.x0), h = Math.abs(e.clientY - drag.y0);
        setDrag(null);
        if (w > 6 && h > 6) setPins(p => [...p, { id: pinSeq.current++, kind: 'region', label: `region ${Math.round(w)}×${Math.round(h)}`, note: '', rect: { x, y, w, h } }]);
      }
    };
    const onClick = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (isUI(el)) return;
      e.preventDefault(); e.stopPropagation();
      const t = toolRef.current;
      if (t === 'point') {
        const r = el!.getBoundingClientRect();
        setPins(p => [...p, { id: pinSeq.current++, kind: 'point', label: describe(el!), note: '', rect: { x: r.left, y: r.top, w: r.width, h: r.height } }]);
      } else if (t === 'marker') {
        setPins(p => [...p, { id: pinSeq.current++, kind: 'marker', label: `marker @ ${Math.round(e.clientX)},${Math.round(e.clientY)}`, note: '', rect: { x: e.clientX, y: e.clientY, w: 0, h: 0 } }]);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finishPick(); };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking, drag]);

  // Inside an admin's portal-preview window the session user is the CLIENT or
  // EMPLOYEE being previewed, so the normal role gate would hide the button.
  // The parent tab is an authenticated admin by construction, and the request
  // is filed with THEIR token — so allow it there.
  const preview = isEmbeddedPreview();
  if (isDemo()) return null;
  if (!preview && (!user || !['owner', 'admin', 'pm'].includes(user.role))) return null;

  const match = APP_PAGES
    .filter(p => p.route && pathname.startsWith(p.route))
    .sort((a, b) => b.route.length - a.route.length)[0];
  const pageName = preview
    ? `Portal preview — ${match?.name || pathname}`
    : (match?.name || pathname);

  const startPick = () => { setOpen(false); setPicking(true); };
  function finishPick() { setPicking(false); setHover(null); setDrag(null); setOpen(true); }
  const removePin = (id: number) => setPins(p => p.filter(x => x.id !== id));
  const setPinNote = (id: number, note: string) => setPins(p => p.map(x => x.id === id ? { ...x, note } : x));

  // Screenshot the page with the annotation overlays baked in (#71)
  async function captureAnnotated(): Promise<File | null> {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        ignoreElements: (el) => el.hasAttribute?.('data-cr-ui'),
        useCORS: true, logging: false, scale: Math.min(1, window.devicePixelRatio || 1),
        x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight,
      });
      const ctx = canvas.getContext('2d');
      if (ctx) {
        pins.forEach((p, i) => {
          ctx.strokeStyle = '#C77D3F'; ctx.fillStyle = 'rgba(199,125,63,0.15)'; ctx.lineWidth = 3;
          if (p.kind === 'marker') {
            ctx.beginPath(); ctx.arc(p.rect.x, p.rect.y, 10, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          } else {
            ctx.fillRect(p.rect.x, p.rect.y, p.rect.w, p.rect.h);
            ctx.strokeRect(p.rect.x, p.rect.y, p.rect.w, p.rect.h);
          }
          ctx.fillStyle = '#C77D3F';
          ctx.beginPath(); ctx.arc(p.rect.x, p.rect.y, 11, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#fff'; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(String(i + 1), p.rect.x, p.rect.y);
        });
      }
      const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/png', 0.9));
      return blob ? new File([blob], 'annotated-page.png', { type: 'image/png' }) : null;
    } catch { return null; }
  }

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const shot = pins.length ? await captureAnnotated() : null;
      const pinText = pins.length
        ? '\n\nAnnotations on the page:\n' + pins.map((p, i) =>
            `  ${i + 1}. [${p.kind}] ${p.label}${p.note.trim() ? ` — ${p.note.trim()}` : ''}`).join('\n')
        : '';
      const form = new FormData();
      form.append('page', pageName);
      form.append('title', title.trim());
      form.append('details', `${details.trim()}${pinText}\n\n(filed from ${pathname})`);
      form.append('priority', priority);
      if (shot) form.append('attachment', shot);
      else if (file) form.append('attachment', file);
      // In a preview window, authenticate as the admin in the parent tab —
      // the realm's own token belongs to the previewed client/employee.
      const adminTok = getParentAdminToken();
      const authHdr = adminTok ? { Authorization: `Bearer ${adminTok}` } : {};
      const r = await apiClient.post('/dev/change-requests', form, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHdr },
      });
      setTitle(''); setDetails(''); setPriority('normal'); setFile(null); setPins([]);
      setSent(true);
      const id = r.data?.data?.id as number | undefined;
      setTimeout(() => {
        setSent(false); setOpen(false);
        if (id && window.confirm('Queued ✓ — fulfill NOW? (OK = Claude picks it up within ~15 min; Cancel = daily run)')) {
          apiClient.put(`/dev/change-requests/${id}`, { fulfill_now: true, priority: 'high' }, { headers: authHdr }).catch(() => {});
        }
      }, 900);
    } finally { setBusy(false); }
  };

  const regionRect = drag && { x: Math.min(drag.x0, drag.x), y: Math.min(drag.y0, drag.y), w: Math.abs(drag.x - drag.x0), h: Math.abs(drag.y - drag.y0) };

  return (
    <>
      {!picking && (
        <button onClick={() => setOpen(true)}
          title={preview ? 'Request a change to this portal page' : 'Request a change to this page'}
          data-cr-ui
          // In a portal preview, sit above the portal's bottom tab bar.
          className={`fixed left-4 z-40 h-10 w-10 rounded-full bg-white border shadow-lg flex items-center justify-center active:scale-95 transition-all ${
            preview
              ? 'bottom-20 border-[#C77D3F]/50 text-[#C77D3F]'
              : 'bottom-4 border-gray-200 text-gray-400 hover:text-[#C77D3F] hover:border-[#C77D3F]/40'
          }`}>
          <span className="material-symbols-outlined text-xl">flag</span>
        </button>
      )}

      {(picking || open) && pins.map((p, i) => (
        <div key={p.id} data-cr-ui className="fixed z-[55] pointer-events-none border-2 border-[#C77D3F] rounded"
          style={p.kind === 'marker'
            ? { left: p.rect.x - 8, top: p.rect.y - 8, width: 16, height: 16, borderRadius: 9999, background: 'rgba(199,125,63,0.25)' }
            : { left: p.rect.x, top: p.rect.y, width: p.rect.w, height: p.rect.h, background: 'rgba(199,125,63,0.1)' }}>
          <span className="absolute -top-2.5 -left-2.5 h-5 w-5 rounded-full bg-[#C77D3F] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
        </div>
      ))}

      {picking && (
        <>
          {tool === 'point' && hover && (
            <div className="fixed z-[54] pointer-events-none border-2 border-dashed border-[#5C7F4F] bg-[#5C7F4F]/10 rounded"
              style={{ left: hover.x, top: hover.y, width: hover.w, height: hover.h }} />
          )}
          {tool === 'region' && regionRect && (
            <div className="fixed z-[54] pointer-events-none border-2 border-[#5C7F4F] bg-[#5C7F4F]/10"
              style={{ left: regionRect.x, top: regionRect.y, width: regionRect.w, height: regionRect.h }} />
          )}
          <div data-cr-ui className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] bg-[#2C1810] text-white rounded-full shadow-2xl px-3 py-2 flex items-center gap-2 text-sm">
            {([['point', 'ads_click', 'Element'], ['region', 'crop_square', 'Region'], ['marker', 'location_on', 'Marker']] as const).map(([t, ic, lbl]) => (
              <button key={t} onClick={() => setTool(t)}
                className={`h-7 px-2.5 rounded-full text-xs font-semibold flex items-center gap-1 ${tool === t ? 'bg-[#C77D3F] text-white' : 'text-white/70 hover:text-white'}`}>
                <span className="material-symbols-outlined text-sm">{ic}</span>{lbl}
              </button>
            ))}
            <span className="text-white/50 text-xs">{pins.length} flagged</span>
            <button onClick={finishPick} className="h-7 px-3 rounded-full bg-[#5C7F4F] text-white text-xs font-bold">Done</button>
          </div>
        </>
      )}

      {open && (
        <div data-cr-ui className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#C77D3F]">flag</span>
                Request a change here
                {sent && <span className="text-green-700 text-xs font-semibold">✓ queued</span>}
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5">{pageName} · <code>{pathname}</code></p>
            </div>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              placeholder="What should change?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3}
              placeholder="Details (optional — or annotate the page below)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />

            <button onClick={startPick}
              className="w-full h-9 rounded-lg border border-[#C77D3F]/40 text-[#8a5526] text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-[#FAF6F0]">
              <span className="material-symbols-outlined text-base">draw</span>
              {pins.length ? `Annotate more (${pins.length} flagged)` : 'Annotate the page — point, region, marker'}
            </button>
            {pins.length > 0 && (
              <div className="space-y-1.5">
                {pins.map((p, i) => (
                  <div key={p.id} className="border border-gray-200 rounded-lg p-2">
                    <div className="flex items-center gap-1.5">
                      <span className="h-4 w-4 rounded-full bg-[#C77D3F] text-white text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <code className="text-[11px] text-gray-500 flex-1 truncate">{p.kind} · {p.label}</code>
                      <button onClick={() => removePin(p.id)} className="text-gray-300 hover:text-red-500">
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                    <input value={p.note} onChange={e => setPinNote(p.id, e.target.value)}
                      placeholder="What about this?"
                      className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none" />
                  </div>
                ))}
                <p className="text-[10px] text-gray-400">A screenshot of the annotated page attaches automatically on submit.</p>
              </div>
            )}

            <div className="flex items-center gap-1.5 flex-wrap">
              {(['low', 'normal', 'high'] as const).map(pr => (
                <button key={pr} onClick={() => setPriority(pr)}
                  className={`h-8 px-2.5 rounded-lg text-xs font-semibold border capitalize ${priority === pr ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {pr}
                </button>
              ))}
              <button onClick={() => fileRef.current?.click()}
                className={`h-8 px-2.5 rounded-lg border text-xs font-semibold ${file ? 'border-[#C77D3F] text-[#C77D3F]' : 'border-gray-200 text-gray-400'}`}>
                📎 {file ? file.name.slice(0, 14) : 'Attach'}
              </button>
              <input ref={fileRef} type="file" className="sr-only" onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOpen(false)} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={submit} disabled={busy || !title.trim()}
                className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
                {busy ? (pins.length ? 'Capturing…' : 'Queuing…') : 'Queue it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
